import axios, { AxiosInstance } from "axios";
import { LoginDto, LoginSuccessDto } from "../types/auth";
import { UserPool } from "../types/userPool";
import Api from "./api";
import { UserListDto } from "../types/user";

class AuthService {
  private authApi: AxiosInstance;
  private api: Api;

  constructor() {
    this.authApi = axios.create({
      baseURL: process.env.REACT_APP_API_AUTH,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });
    this.api = new Api();

    // Adicionar interceptor para incluir token de autenticação
    this.authApi.interceptors.request.use((config: any) => {
      const token = localStorage.getItem("tbi_access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Adicionar interceptor para tratar erros de autenticação
    this.authApi.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  async login(data: {
    user: string;
    password: string;
  }): Promise<LoginSuccessDto> {
    const response = await this.authApi.post(`/login`, data, {
      headers: {
        Refer: window.location.origin,
      },
    });
    return response.data;
  }

  async loginWithJWT(token: string): Promise<LoginSuccessDto> {
    const response = await this.authApi.get(`/login/${token}`);
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<LoginSuccessDto> {
    const response = await this.authApi.post("/refresh", {
      refreshToken,
    });
    return response.data;
  }

  async updatePassword(data: any) {
    return this.authApi.post(`/user/force-newpassword`, data, {});
  }

  async getUserPoolOrigin(): Promise<UserPool[]> {
    const response = await this.authApi.get<UserPool[]>("/user-pool-origin");
    return response.data;
  }
  async getUserList(origin: string): Promise<UserListDto> {
    const response = await this.authApi.get<UserListDto>(`/user/list/bko?origin=${origin}`);
    return response.data;
  }

  // User endpoints
  async updateUser(email: string, data: any) {
    return this.authApi.put(`/user/update/${email}`, data);
  }

  async getUserByEmail(email: string) {
    return this.authApi.get(`/user/get-user/${email}`);
  }

  async registerUser(data: any) {
    return this.authApi.post(`/user/register`, data);
  }

  async sendEmailConfirmation(email: string) {
    return this.authApi.post(`/user/send-email`, email);
  }

  async resetPassword(data: any) {
    return this.authApi.post(`/user/reset-password`, data);
  }

  async sendInvite(data: any, headers: { 'X-Userid': string; 'X-ClientId': string }) {
    return this.authApi.post(`/user/invite/send`, data, { headers });
  }

  async removeUserAccess(username: string) {
    return this.authApi.delete(`/user/${username}/remove-access`);
  }

  async deleteUser(data: any) {
    return this.authApi.delete(`/user/delete`, { data });
  }

  // User Pool endpoints - Moved to userPoolService.ts
  // User Pool Origin endpoints - Moved to userPoolOriginService.ts

  // Invite endpoints
  async getAllInvites() {
    return this.authApi.get(`/invite`);
  }

  async getInviteById(id: string) {
    return this.authApi.get(`/invite/${id}`);
  }

  async updateInvite(id: string, data: any) {
    return this.authApi.put(`/invite/${id}`, data);
  }

  async deleteInvite(id: string) {
    return this.authApi.delete(`/invite/${id}`);
  }

  async getInvitesByStatus(status: string) {
    return this.authApi.get(`/invite/status/${status}`);
  }

  async getInvitesByInviter(inviterId: string) {
    return this.authApi.get(`/invite/inviter/${inviterId}`);
  }

  // Auth endpoints
  async validateToken(token: string) {
    return this.authApi.get(`/validate-token`, {
      headers: { Authorization: token }
    });
  }

  async decodeToken(token: string) {
    return this.authApi.get(`/decode-token`, {
      headers: { Authorization: token }
    });
  }

  async confirmEmailCode(data: any) {
    return this.authApi.post(`/confirm-code`, data);
  }

  // Cognito Manager endpoints - Moved to cognitoManagerService.ts

  // Access Group endpoints
  async createAccessGroup(data: any) {
    return this.authApi.post(`/acess-group/create`, data);
  }

  async listAccessGroups() {
    return this.authApi.get(`/acess-group/groups`);
  }

  async deleteAccessGroup(data: any) {
    return this.authApi.delete(`/acess-group/delete`, { data });
  }
}

export const authService = new AuthService();
